import { type EffectCallback, useEffect } from "react";

/**
 * Wrapper around useEffect with an empty dependency array.
 * Makes the intent explicit: this effect runs once on mount
 * and optionally cleans up on unmount.
 *
 * Direct useEffect usage is banned -- use this for one-time
 * external system synchronization (DOM, third-party widgets,
 * browser APIs).
 */
// eslint-disable-next-line react-hooks/exhaustive-deps
export function useMountEffect(effect: EffectCallback): void {
	// Deps MUST be `[]`, not `[effect]`: callers pass inline closures whose
	// identity changes every render, so `[effect]` made every "mount" effect
	// clean up and re-run on EACH render. Effects that write reactive state
	// (zustand bridges, stores) then fed a render→effect→render cascade that
	// blew React's nested-update limit ("Maximum update depth exceeded", e.g.
	// DocumentsSectionLayout's fullscreen-bridge register/unregister churn).
	// biome-ignore lint/correctness/useExhaustiveDependencies: mount-once by design — adding `effect` re-runs it every render (the bug above). eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(effect, []);
}
